<template>
  <div>
    <MainLayout :hide-route-header="true">
      <ContactNotFound v-if="hasError" entity="Teammate" />
      <div v-else-if="contact" class="max-w-3xl mx-auto lg:max-w-7xl space-y-6">
        <div class="hidden lg:flex lg:items-center lg:justify-between lg:gap-6">
          <PersonHeader :person="contact">
            <template #badges>
              <Badge :variant="contact.status == 'Active' ? 'green' : 'red'">{{ contact.status }}</Badge>
            </template>
          </PersonHeader>
          <TeammateActions class="shrink-0 justify-end" :record="contact" />
        </div>
        <div class="lg:hidden">
          <PersonHeader :person="contact">
            <template #badges>
              <Badge :variant="contact.status == 'Active' ? 'green' : 'red'">{{ contact.status }}</Badge>
            </template>
          </PersonHeader>
          <TeammateActions class="mt-6 justify-start" :record="contact" />
        </div>
        <LiveContactEventAlert :events="contact.events" />
        <div class="grid grid-cols-1 gap-6 lg:grid-flow-col-dense lg:grid-cols-3">
          <div class="space-y-6 lg:col-start-1 lg:col-span-2">
            <PersonCustomFields
              :person="contact"
              :custom-fields-list="customFieldsForEntity"
              @custom-fields-updated="getReload"
            />

            <div
              v-if="Object.keys(contact.customAttributes).length > 0"
              class="mt-6 bg-white shadow overflow-hidden sm:rounded-lg"
            >
              <div class="px-4 py-5 border-b border-gray-200 sm:px-6 flex flex-wrap justify-between">
                <div>
                  <h3 class="inline-block text-lg leading-6 font-medium text-gray-900">Custom fields</h3>
                </div>
              </div>
              <div class="px-4 py-5 sm:px-6">
                <dl class="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-4">
                  <div v-for="key in Object.keys(contact.customAttributes)" :key="key" class="sm:col-span-1">
                    <dt class="text-sm leading-5 font-medium text-gray-500">
                      {{ key }}
                    </dt>
                    <dd class="mt-1 text-sm leading-5 text-gray-900">
                      {{ contact.customAttributes[key] ? contact.customAttributes[key] : 'null' }}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>

            <Card>
              <CardHeader class="flex justify-between items-center">
                <CardTitle>Pipeline Reviews</CardTitle>
                <router-link :to="`/team/${$route.params.id}/pipeline_reviews/new`">
                  <Button variant="card-header-action" size="sm">New Review</Button>
                </router-link>
              </CardHeader>
              <CardContent>
                <div v-if="pipelineReviewConfig" class="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-3">
                  <div class="space-y-2">
                    <dt class="text-xs font-medium uppercase tracking-[0.08em] text-gray-500">Reviewer</dt>
                    <dd>
                      <Select
                        :model-value="String(pipelineReviewConfig.reviewer_id)"
                        @update:model-value="updatePipelineReviewConfig({ reviewer_id: Number($event) })"
                      >
                        <SelectTrigger class="h-8 w-full text-sm">
                          <SelectValue :placeholder="pipelineReviewConfig.reviewer_display_name || 'Unassigned'" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem
                            v-for="member in adminContributorMembers"
                            :key="member.id"
                            :value="String(member.id)"
                          >
                            {{ member.name }} ({{ member.email }})
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </dd>
                  </div>
                  <div class="space-y-2">
                    <dt class="text-xs font-medium uppercase tracking-[0.08em] text-gray-500">Cadence</dt>
                    <dd>
                      <PipelineReviewCadenceSelect
                        :model-value="pipelineReviewConfig.cadence"
                        @update:model-value="updatePipelineReviewConfig({ cadence: $event as PipelineReviewCadence })"
                      />
                    </dd>
                  </div>
                  <div class="space-y-2">
                    <dt class="text-xs font-medium uppercase tracking-[0.08em] text-gray-500">Active</dt>
                    <dd class="min-h-6 flex items-center">
                      <Switch
                        :model-value="pipelineReviewConfig.active"
                        @update:model-value="updatePipelineReviewConfig({ active: $event })"
                      />
                    </dd>
                  </div>
                </div>
                <div class="mt-6">
                  <TeammatePipelineReviews />
                </div>
              </CardContent>
            </Card>

            <Card class="px-4 py-5 sm:px-6" extra-classes="overflow-visible">
              <CardContent>
                <Tabs default-value="activity">
                  <TabsList>
                    <TabsTrigger value="activity">Activity</TabsTrigger>
                    <TabsTrigger value="events">Events</TabsTrigger>
                  </TabsList>
                  <TabsContent value="activity">
                    <TeammateActivity
                      :contact="contact"
                      @activity-created="getNotesAndEvents()"
                      @activity-deleted="getNotesAndEvents()"
                    />
                  </TabsContent>
                  <TabsContent value="events">
                    <TeammateEvents :contact="contact" />
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>

          <div>
            <section aria-labelledby="task-overview-title">
              <TaskOverviewCard :contact-id="contactId" :tasks-query="tasksQuery" />
            </section>
            <section aria-labelledby="enrichment-title" class="mt-6">
              <TeammateEnrichment :contact="contact" />
            </section>
            <section aria-labelledby="referrals-title" class="lg:col-start-3 lg:col-span-1">
              <TeammateReferrals :contact="contact" />
            </section>
            <section aria-labelledby="attachments-title" class="mt-6 lg:col-start-3 lg:col-span-1">
              <Attachments :contact-id="contact.id" :files="contact.files" @reload="getReload" />
            </section>
          </div>
        </div>
      </div>

      <div
        v-if="$route.query.history"
        class="mt-6 max-w-3xl mx-auto grid grid-cols-1 gap-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-1"
      >
        <History :contact-id="contactId" />
      </div>
    </MainLayout>
  </div>
</template>

<script setup lang="ts">
/* eslint-disable @typescript-eslint/no-explicit-any */

import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import DateUtility from '../../utilities/dates';
import MainLayout from '@/components/layouts/MainLayout/index.vue';
import PersonHeader from '@/components/person/PersonHeader/index.vue';
import PersonCustomFields from '@/components/person/PersonCustomFields/index.vue';
import TaskOverviewCard from '../person/TaskOverviewCard/index.vue';
import TeammateActions from './TeammateActions.vue';
import TeammateActivity from './TeammateActivity.vue';
import TeammateEnrichment from './TeammateEnrichment.vue';
import TeammateEvents from './TeammateEvents.vue';
import History from '../person/History.vue';
import TeammatePipelineReviews from './TeammatePipelineReviews.vue';
import TeammateReferrals from './TeammateReferrals.vue';
import LiveContactEventAlert from '@/components/shared/LiveContactEventAlert.vue';
import Attachments from '../person/Attachments.vue';
import ContactNotFound from '../contact/ContactNotFound.vue';
import { Api } from '@/api';
import { useLoadingSpinnerStore } from '@/stores/loadingSpinner';
import { useTeamStore } from '@/stores/team';
import PipelineReviewCadenceSelect from '../shared/PipelineReviewCadenceSelect.vue';
import { useTeammatePipelineReviews } from './useTeammatePipelineReviews';
import { useHFetchQuery } from '@/lib/hfetch/useHFetchQuery';
import type { Person } from '../person/Person';
import type { CustomFieldEntity } from '@/api/entity_types/CustomField';
import type { ActiveStorageBlobEntity } from '@/api/entity_types/ActiveStorageBlob';
import type { PipelineReviewCadence } from '@/api/entity_types/pipeline_review/ContactConfiguration';

type TeammateDetail = Person & {
  firstName: string;
  lastName: string;
  status: string;
  created: string;
  referralCode: string;
  referralLink: string;
  customAttributes: Record<string, unknown>;
  activities: any[];
  files: ActiveStorageBlobEntity[];
  referrals: any[];
  events: any[];
};

const route = useRoute();
const loadingSpinnerStore = useLoadingSpinnerStore();
const teamStore = useTeamStore();
const {
  config: pipelineReviewConfig,
  setBaseConfig: setBasePipelineReviewConfig,
  updateField: updatePipelineReviewConfig,
} = useTeammatePipelineReviews();

const contactId = computed(() => route.params.id as string);

const events = ref<any[]>([]);
const activities = ref<any[]>([]);

const teammateQuery = useHFetchQuery(async () => {
  const response = await fetch(`/api/teammates/${contactId.value}`);
  if (!response.ok) return { status: 'error', messages: ['Teammate not found'] };
  const data = await response.json();
  setBasePipelineReviewConfig(data.pipeline_review_contact_configuration);
  return {
    status: 'success',
    data: {
      teammate: {
        id: data.record.id,
        name: data.record.name,
        firstName: data.record.first_name,
        lastName: data.record.last_name,
        email: data.record.email,
        phone: data.record.phone_number,
        status: data.record.teammate_status,
        license: data.record.re_license_number,
        canReceiveSms: data.record['can_receive_sms?'] ?? true,
        created: DateUtility.formatDate(data.record.created_at, 'MMMM dd yyyy, h:mm a') ?? '',
        referralCode: data.record.referral_code,
        referralLink: data.referral_link,
        customAttributes: data.record.jsonb,
        files: data.files,
        referrals: data.referrals,
        customFields: data.record.custom_field_values || [],
      },
      customFieldsForEntity:
        data.custom_fields_for_entity?.sort((a: CustomFieldEntity, b: CustomFieldEntity) =>
          a.name.localeCompare(b.name)
        ) ?? [],
    },
  };
});

const tasksQuery = useHFetchQuery(() => Api.Contacts.getContactTasks({ id: +contactId.value }));

const contact = computed<TeammateDetail | null>(() =>
  teammateQuery.data.value
    ? { ...teammateQuery.data.value.teammate, events: events.value, activities: activities.value }
    : null
);
const customFieldsForEntity = computed(() => teammateQuery.data.value?.customFieldsForEntity ?? []);
const hasError = computed(() => !!teammateQuery.errors.value);

const adminContributorMembers = computed(() => teamStore.allTeamMembers);

const getReload = async () => {
  await Promise.all([teammateQuery.reload(), getNotesAndEvents()]);
};

const getNotesAndEvents = async () => {
  loadingSpinnerStore.showLoadingSpinner();
  try {
    const [notes, emails, contactEvents] = await Promise.all([
      fetch(`/api/contact_notes/${contactId.value}`).then(response => response.json()),
      fetch(`/api/contact_emails/${contactId.value}`).then(response => response.json()),
      fetch(`/api/contact_events?contact_id=${contactId.value}`).then(response => response.json()),
    ]);
    buildTimeline(notes, emails, contactEvents);
  } finally {
    loadingSpinnerStore.hideLoadingSpinner();
  }
};

const buildTimeline = (notes: any[], emails: any[], contactEvents: any[]) => {
  const fullTimeline: any[] = [];
  const newEvents: any[] = [];
  for (const note of notes) {
    fullTimeline.push({ activityType: 'note', sortDate: note.created_at, ...note });
  }
  for (const email of emails) {
    fullTimeline.push({ activityType: 'email', sortDate: email.created_at, ...email });
  }
  for (const event of contactEvents) {
    newEvents.push({ activityType: 'event', sortDate: event.start_time, ...event });
  }
  fullTimeline.sort((a, b) => new Date(b.sortDate).getTime() - new Date(a.sortDate).getTime());
  events.value = newEvents;
  activities.value = fullTimeline;
};

onMounted(() => {
  getNotesAndEvents();
});
</script>
